import { isStripeCheckout } from '../../lib/checkout-mode.js';
import { priceDisplayMode } from '../../lib/price.js';
import { Phone, Mail, MapPin, ShieldCheck } from 'lucide-react';
import { TenantLogo } from '../tenant/TenantLogo.jsx';
import { showInternalTools } from '../../lib/internal-tools.js';
import { useTenant, DEFAULT_FALLBACK_TENANT } from '../../context/TenantContext.jsx';
import { SHOP_CATEGORIES } from '../../lib/shop-taxonomy.js';
import { useContentBlocks, resolveContentValue, CONTENT_FALLBACKS } from '../../lib/content.js';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { EditableText } from '../editing/EditableText.jsx';

export function ShellFooter({ onNavigate, tenant: propTenant }) {
  let tenant = propTenant;
  try {
    const ctx = useTenant();
    if (!tenant) tenant = ctx?.tenant;
  } catch {
    if (!tenant) tenant = DEFAULT_FALLBACK_TENANT;
  }
  if (!tenant) tenant = DEFAULT_FALLBACK_TENANT;
  const currentYear = new Date().getFullYear();
  // Demo-Tenants: interne Einstiege (Hub, Control-Plane) ausblenden (qa-only F-01)
  const showInternal = showInternalTools(tenant?.settings);

  const { revision } = useEditMode();
  const { map: footerMap } = useContentBlocks('footer', tenant?.slug, { revision });

  const address = tenant?.address;
  const companyName = address?.company || tenant?.name || 'Partner-Shop';
  const cleanPhone = tenant?.phone ? tenant.phone.replace(/[^0-9+]/g, '') : '';

  const defaultDescription = tenant?.settings?.branding?.footerDescription ||
    `${companyName} ist Ihr Partner für professionelle Sicherheitstechnik, Brandmeldetechnik und Systemlösungen.`;
  const footerDescriptionRaw = resolveContentValue(footerMap, 'footer_description', '');
  const footerDescription = footerDescriptionRaw || defaultDescription;

  const footerPartnerBadgeRaw = resolveContentValue(footerMap, 'footer_partner_badge', '');
  const footerPartnerBadge = footerPartnerBadgeRaw || CONTENT_FALLBACKS.footer.footer_partner_badge;

  const footerB2bNoticeRaw = resolveContentValue(footerMap, 'footer_b2b_notice', '');
  // a-055b: Hinweis folgt dem Preismodus (Kauf brutto -> inkl., Anfrage/OCI netto -> zzgl.)
  const footerB2bNotice = footerB2bNoticeRaw || (priceDisplayMode(tenant) === 'gross'
    ? 'Alle Preise inkl. gesetzlicher MwSt. • Sicherheitstechnik für Zuhause und Betrieb'
    : 'Alle Preise zzgl. gesetzlicher MwSt. • Sicherheitstechnik für Zuhause und Betrieb');

  const defaultCopyright = `© ${currentYear} ${companyName}. Alle Rechte vorbehalten.`;
  const footerCopyrightRaw = resolveContentValue(footerMap, 'footer_copyright', '');
  const footerCopyright = footerCopyrightRaw || defaultCopyright;

  return (
    <footer className="border-t border-[var(--border)] bg-white text-[var(--foreground)]">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          
          {/* Col 1: Brand & Description */}
          <div className="space-y-4">
            <TenantLogo showSlogan={false} />
            <p className="text-sm leading-relaxed text-[var(--secondary)]">
              <EditableText
                page="footer"
                blockKey="footer_description"
                value={footerDescriptionRaw}
                multiline={true}
                data-testid="edit-footer-description"
              >
                {footerDescription}
              </EditableText>
            </p>
            <div className="flex items-center gap-2 text-xs font-semibold text-[var(--primary)]">
              <ShieldCheck className="h-4 w-4 shrink-0" />
              <EditableText
                page="footer"
                blockKey="footer_partner_badge"
                value={footerPartnerBadgeRaw}
                data-testid="edit-footer-partner-badge"
              >
                {footerPartnerBadge}
              </EditableText>
            </div>
          </div>

          {/* Col 2: Categories */}
          <div className="space-y-4">
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
              Fachkatalog
            </h4>
            <ul className="space-y-2 text-sm text-[var(--secondary)]">
              {SHOP_CATEGORIES.map((cat) => (
                <li key={cat.slug}>
                  <button
                    onClick={() => onNavigate(`/catalog/${cat.slug}`)}
                    className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                  >
                    {cat.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Contact Details */}
          <div className="space-y-4">
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
              Kontakt & Beratung
            </h4>
            <div className="space-y-3 text-sm text-[var(--secondary)]">
              {tenant?.phone && (
                <div className="flex items-center gap-2.5">
                  <Phone className="h-4 w-4 text-[var(--primary)] shrink-0" />
                  <a
                    href={`tel:${cleanPhone}`}
                    className="hover:text-[var(--primary)] transition-colors font-medium"
                  >
                    {tenant.phone}
                  </a>
                </div>
              )}

              {tenant?.contactEmail && (
                <div className="flex items-center gap-2.5">
                  <Mail className="h-4 w-4 text-[var(--primary)] shrink-0" />
                  <a
                    href={`mailto:${tenant.contactEmail}`}
                    className="hover:text-[var(--primary)] transition-colors font-medium break-all"
                  >
                    {tenant.contactEmail}
                  </a>
                </div>
              )}

              {address && (
                <div className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-[var(--primary)] shrink-0 mt-0.5" />
                  <span className="leading-snug">
                    {typeof address === 'string' ? (
                      address
                    ) : (
                      <>
                        {address.company && <span className="block font-semibold">{address.company}</span>}
                        {address.street && <span className="block">{address.street}</span>}
                        {(address.zip || address.city) && (
                          <span className="block">
                            {address.zip} {address.city}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Col 4: Legal & Service */}
          <div className="space-y-4">
            <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
              Rechtliches & Service
            </h4>
            <ul className="space-y-2 text-sm text-[var(--secondary)]">
              <li>
                <button
                  onClick={() => onNavigate('/impressum')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  Impressum
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('/datenschutz')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  Datenschutzerklärung
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('/agb')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  AGB
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('/widerruf')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  Widerrufsbelehrung
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('/kontakt')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  Kontakt
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('/versand')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  Versand & Lieferung
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('/cart')}
                  className="hover:text-[var(--primary)] transition-colors cursor-pointer text-left"
                >
                  {isStripeCheckout(tenant) ? 'Warenkorb einsehen' : 'Anfragekorb einsehen'}
                </button>
              </li>
              {showInternal && (
              <li>
                <button
                  onClick={() => onNavigate('/hub')}
                  data-testid="executive-hub-footer-link"
                  className="font-semibold text-[var(--primary)] hover:underline transition-colors cursor-pointer text-left"
                >
                  Betriebsleiter Hub &rarr;
                </button>
              </li>
              )}
            </ul>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-[var(--border)] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[var(--secondary)]">
          <p>
            <EditableText
              page="footer"
              blockKey="footer_copyright"
              value={footerCopyrightRaw}
              data-testid="edit-footer-copyright"
            >
              {footerCopyright}
            </EditableText>
          </p>
          <div className="flex items-center gap-4">
            <EditableText
              page="footer"
              blockKey="footer_b2b_notice"
              value={footerB2bNoticeRaw}
              data-testid="edit-footer-b2b-notice"
            >
              {footerB2bNotice}
            </EditableText>
            {showInternal && (
              <>
                <span>•</span>
                <button
                  onClick={() => onNavigate('/hub')}
                  data-testid="executive-hub-footer-trigger"
                  className="text-slate-400 hover:text-[var(--primary)] transition-colors cursor-pointer font-medium"
                >
                  Betriebsleiter Hub
                </button>
                <span>•</span>
                <button
                  onClick={() => onNavigate('/admin')}
                  className="text-slate-400 hover:text-[var(--primary)] transition-colors cursor-pointer"
                >
                  Control-Plane
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
